// src/pages/PreisePage.js

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Container,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  alpha,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';

const PreisePage = () => {
  const navigate = useNavigate();

  const pricingPlans = [
    {
      name: 'Alpha Basis',
      tagline: 'Aktuell inklusive',
      price: 0,
      period: 'kostenlos',
      popular: false,
      description: 'Die aktuelle Alpha-Version ist komplett kostenlos nutzbar – ohne Kreditkarte und ohne Checkout.',
      features: [
        { text: 'Immobilien, Dokumente und Finanzen zentral verwalten', included: true },
        { text: 'Multi-User-Zugriff und Teamarbeit im aktuellen Alpha-Status', included: true },
        { text: 'Betriebs- und Nebenkostenprozesse ohne zusätzliche Hürde nutzen', included: true },
        { text: 'Schneller Einstieg und freie Nutzung für Test- und Pilotprojekte', included: true }
      ],
      cta: 'Jetzt kostenlos starten',
      ctaVariant: 'outlined'
    },
    {
      name: 'Alpha Verwaltung',
      tagline: 'Für alle Teams',
      price: 0,
      period: 'kostenlos',
      popular: true,
      description: 'Alle verfügbaren Verwaltungsfunktionen sind im Alpha-Status aktuell kostenlos zugänglich.',
      features: [
        { text: 'Dokumenten- und Vertragspflege direkt im System', included: true },
        { text: 'Übersichtliche Buchhaltungs- und Reporting-Funktionen', included: true },
        { text: 'Sichere Rollen- und Rechteverwaltung für mehrere Nutzer', included: true },
        { text: 'Keine Zahlungssperre oder Freischaltung nötig', included: true }
      ],
      cta: 'Jetzt kostenlos testen',
      ctaVariant: 'contained',
      note: 'Das Billing-System ist für die aktuelle Alpha-Phase deaktiviert.'
    },
    {
      name: 'Alpha Erweiterungen',
      tagline: 'Ohne Kaufzwang',
      price: 0,
      period: 'kostenlos',
      perUnit: false,
      popular: false,
      description: 'Erweiterte Funktionen bleiben im aktuellen Stand offen und frei nutzbar.',
      features: [
        { text: 'OCR- und Dokumentenprozesse ohne gesonderte Freischaltung', included: true },
        { text: 'Erweiterte Auswertungen und Vorschlagsfunktionen', included: true },
        { text: 'Pilot- und Test-Setup ohne Preisbarriere', included: true },
        { text: 'Flexibles Ausprobieren von neuen Funktionen', included: true }
      ],
      cta: 'Jetzt starten',
      ctaVariant: 'outlined',
      addon: true,
      note: 'Bezahlmodelle werden zu einem späteren Zeitpunkt separat aktiviert.'
    }
  ];

  const additionalInfo = [
    {
      title: 'Aktuell komplett kostenlos',
      description: 'Die Alpha-Version ist ohne Bezahlung nutzbar und bleibt frei zugänglich.'
    },
    {
      title: 'Keine Kreditkarte nötig',
      description: 'Es gibt aktuell keinen Checkout- oder Kaufprozess für die normale Nutzung.'
    },
    {
      title: 'Mehrere Nutzer möglich',
      description: 'Team- und Rollenverwaltung sind im aktuellen Stand bereits direkt verfügbar.'
    },
    {
      title: 'Später flexibel ausbauen',
      description: 'Bezahlmodule können später separat aktiviert werden, sobald sie produktreif sind.'
    }
  ];

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Navigation Bar */}
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{ 
          bgcolor: '#2c3e50',
          borderBottom: 'none'
        }}
      >
        <Toolbar sx={{ py: 1.5 }}>
          <Typography 
            variant="h5" 
            sx={{ 
              flexGrow: 1, 
              fontWeight: 700,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer'
            }}
            onClick={() => navigate('/')}
          >
            <AccountBalanceIcon sx={{ color: '#00bcd4' }} /> Immo-Automation
          </Typography>
          <Button 
            sx={{ color: 'white', mr: 2, textTransform: 'none' }}
            onClick={() => navigate('/features')}
          >
            Funktionen
          </Button>
          <Button 
            sx={{ color: 'white', mr: 2, textTransform: 'none' }}
            onClick={() => navigate('/preise')}
          >
            Preise
          </Button>
          <Button 
            sx={{ color: 'white', mr: 2, textTransform: 'none' }}
            onClick={() => navigate('/kontakt')}
          >
            Hilfe & Kontakt
          </Button>
          <Button 
            variant="outlined"
            onClick={() => navigate('/login')}
            sx={{ 
              borderColor: 'white',
              color: 'white',
              borderRadius: 8,
              px: 3,
              textTransform: 'none',
              '&:hover': {
                borderColor: '#00bcd4',
                bgcolor: alpha('#00bcd4', 0.1)
              }
            }}
          >
            Login
          </Button>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
          color: 'white',
          py: { xs: 6, md: 8 },
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 3, fontSize: { xs: '2.25rem', md: '3rem' }, color: 'white' }}>
            Transparent und fair
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 1, fontWeight: 400, color: 'rgba(255,255,255,0.95)', fontSize: '1.1rem' }}>
            Die aktuelle Alpha-Version ist kostenlos und ohne Kaufzwang nutzbar.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Chip
              label="Alpha / Free-Mode aktiv"
              size="small"
              sx={{
                bgcolor: '#00bcd4',
                color: 'white',
                fontWeight: 700,
                px: 1.5,
                py: 0.75
              }}
            />
          </Box>
        </Container>
      </Box>

      {/* Pricing Cards */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 }, mt: -6 }}>
        <Grid container spacing={4}>
          {pricingPlans.map((plan, index) => (
            <Grid item xs={12} md={plan.name === 'Enterprise' ? 12 : 4} key={index}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%',
                  position: 'relative',
                  border: plan.popular 
                    ? '4px solid #00bcd4' 
                    : '1px solid #cbd5e1',
                  borderRadius: 2,
                  bgcolor: 'white',
                  transform: plan.popular ? 'scale(1.05)' : 'none',
                  transition: 'all 0.2s',
                  boxShadow: plan.popular 
                    ? '0 12px 40px rgba(0,188,212,0.25)' 
                    : '0 2px 8px rgba(0,0,0,0.06)',
                  '&:hover': {
                    transform: plan.popular ? 'scale(1.06)' : 'scale(1.01)',
                    boxShadow: plan.popular 
                      ? '0 16px 48px rgba(0,188,212,0.3)' 
                      : '0 4px 16px rgba(0,0,0,0.1)'
                  }
                }}
              >
                {plan.popular && (
                  <Chip 
                    icon={<StarIcon sx={{ fontSize: 16, color: '#1a365d !important' }} />}
                    label="BELIEBT" 
                    sx={{ 
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      bgcolor: '#fbbf24',
                      color: '#1a365d',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      height: 28,
                      px: 1.5
                    }}
                  />
                )}
                {plan.addon && (
                  <Chip 
                    label="ADD-ON" 
                    sx={{ 
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      bgcolor: '#00bcd4',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      height: 28,
                      px: 1.5
                    }}
                  />
                )}
                
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="overline" sx={{ color: '#00bcd4', fontWeight: 700, fontSize: '0.75rem', letterSpacing: 1.2, display: 'block', mb: 0.5 }}>
                    {plan.tagline}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: '#1a202c', fontSize: '1.875rem' }}>
                    {plan.name}
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    {typeof plan.price === 'number' ? (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 0.5 }}>
                          <Typography 
                            variant="h3" 
                            component="span"
                            sx={{ fontWeight: 700, color: '#1a202c', fontSize: '3rem' }}
                          >
                            {plan.price === 0 ? 'Kostenlos' : `${plan.price.toFixed(2).replace('.', ',')} €`}
                          </Typography>
                          {plan.period !== 'kostenlos' && (
                            <Typography 
                              variant="body1" 
                              component="span"
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              / {plan.period}
                            </Typography>
                          )}
                        </Box>
                        {plan.price === 0 && (
                          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                            aktuell ohne zusätzliche Kosten
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography 
                        variant="h3" 
                        sx={{ fontWeight: 700, color: '#1a202c', fontSize: '3rem' }}
                      >
                        {plan.price}
                      </Typography>
                    )}
                  </Box>

                  <Typography variant="body2" sx={{ color: '#4a5568', mb: 2.5, minHeight: 40, fontSize: '0.9rem', lineHeight: 1.5, fontWeight: 500 }}>
                    {plan.description}
                  </Typography>

                  <Button 
                    variant={plan.ctaVariant}
                    fullWidth
                    size="large"
                    onClick={() => navigate(plan.name === 'Enterprise' ? '/kontakt' : '/register')}
                    sx={{ 
                      py: 1.25,
                      borderRadius: 1.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      mb: 2.5,
                      fontSize: '0.95rem',
                      ...(plan.ctaVariant === 'contained' && {
                        bgcolor: '#2c3e50',
                        color: 'white',
                        '&:hover': {
                          bgcolor: '#34495e'
                        }
                      }),
                      ...(plan.ctaVariant === 'outlined' && {
                        borderColor: '#00bcd4',
                        color: '#00bcd4',
                        borderWidth: 2,
                        '&:hover': {
                          borderWidth: 2,
                          bgcolor: alpha('#00bcd4', 0.08)
                        }
                      })
                    }}
                  >
                    {plan.cta}
                  </Button>

                  <Divider sx={{ mb: 2.5 }} />

                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#1a202c', fontSize: '0.9rem' }}>
                    Funktionen
                  </Typography>

                  <List dense>
                    {plan.features.map((feature, i) => (
                      <ListItem key={i} sx={{ px: 0, py: 0.75, alignItems: 'flex-start' }}>
                        <ListItemIcon sx={{ minWidth: 30, mt: 0.25 }}>
                          {feature.included ? (
                            <CheckCircleIcon sx={{ color: '#00bcd4', fontSize: 20 }} />
                          ) : (
                            <CloseIcon sx={{ color: '#cbd5e1', fontSize: 20 }} />
                          )}
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature.text}
                          primaryTypographyProps={{
                            variant: 'body2',
                            color: feature.included ? '#1a202c' : '#a0aec0',
                            sx: { 
                              textDecoration: feature.included ? 'none' : 'line-through',
                              lineHeight: 1.5,
                              fontSize: '0.9rem',
                              fontWeight: feature.included ? 500 : 400
                            }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  {plan.note && (
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 1.5, fontSize: '0.7rem', lineHeight: 1.3 }}>
                      {plan.note}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Additional Info */}
        <Box sx={{ mt: 8 }}>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', textAlign: 'center' }}>
            * Im aktuellen Alpha-Status sind alle Funktionen kostenlos verfügbar. Spätere Bezahlmodelle werden separat aktiviert.
          </Typography>
        </Box>
      </Container>

      {/* Additional Information Section */}
      <Box sx={{ bgcolor: 'white', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: '#2c3e50' }}>
              Häufig gestellte Fragen zu den Preisen
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {additionalInfo.map((info, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card 
                  elevation={0}
                  sx={{ 
                    height: '100%',
                    border: '2px solid #cbd5e1',
                    borderRadius: 2,
                    p: 3,
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: '#00bcd4',
                      boxShadow: '0 4px 12px rgba(0,188,212,0.1)'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <TrendingUpIcon sx={{ color: '#00bcd4', mr: 2, fontSize: 36 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a202c' }}>
                      {info.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#4a5568', lineHeight: 1.6, fontSize: '0.9rem' }}>
                    {info.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
          color: 'white',
          py: { xs: 8, md: 10 },
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2.5 }}>
            Bereit loszulegen?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, fontWeight: 400, opacity: 0.9 }}>
            Starten Sie noch heute – 14 Tage kostenlos, keine Kreditkarte erforderlich
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button 
              variant="contained"
              size="large"
              onClick={() => navigate('/register')}
              sx={{ 
                bgcolor: '#00bcd4',
                color: 'white',
                px: 6,
                py: 2.5,
                fontSize: '1.2rem',
                borderRadius: 8,
                fontWeight: 700,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: '#00acc1'
                }
              }}
            >
              Jetzt kostenlos testen
            </Button>
            <Button 
              variant="outlined"
              size="large"
              onClick={() => navigate('/kontakt')}
              sx={{ 
                borderColor: 'white',
                color: 'white',
                px: 6,
                py: 2.5,
                fontSize: '1.2rem',
                borderRadius: 8,
                fontWeight: 700,
                textTransform: 'none',
                borderWidth: 2,
                '&:hover': {
                  borderColor: '#00bcd4',
                  bgcolor: alpha('#00bcd4', 0.15),
                  borderWidth: 2
                }
              }}
            >
              Fragen? Kontakt aufnehmen
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box 
        sx={{ 
          bgcolor: '#f8fafc',
          borderTop: '2px solid #e2e8f0',
          py: 6
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1a365d' }}>
                Immo-Automation
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                Moderne Immobilienverwaltung für Vermieter, Hausverwaltungen und Investoren.
              </Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#1a365d' }}>
                Produkt
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" sx={{ color: '#64748b', cursor: 'pointer', '&:hover': { color: '#00bcd4' } }} onClick={() => navigate('/features')}>Funktionen</Typography>
                <Typography variant="body2" sx={{ color: '#64748b', cursor: 'pointer', '&:hover': { color: '#00bcd4' } }} onClick={() => navigate('/preise')}>Preise</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#1a365d' }}>
                Rechtliches
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" sx={{ color: '#64748b', cursor: 'pointer', '&:hover': { color: '#00bcd4' } }} onClick={() => navigate('/impressum')}>Impressum</Typography>
                <Typography variant="body2" sx={{ color: '#64748b', cursor: 'pointer', '&:hover': { color: '#00bcd4' } }} onClick={() => navigate('/datenschutz')}>Datenschutz</Typography>
                <Typography variant="body2" sx={{ color: '#64748b', cursor: 'pointer', '&:hover': { color: '#00bcd4' } }} onClick={() => navigate('/agb')}>AGB</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#1a365d' }}>
                Kontakt
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', cursor: 'pointer', '&:hover': { color: '#00bcd4' } }} onClick={() => navigate('/kontakt')}>
                Kontaktformular
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ my: 4 }} />
          <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center' }}>
            © 2025 Immo-Automation. Alle Rechte vorbehalten.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default PreisePage;
